import SfNavLink from 'components/Common/SfNavLink'
import OffsetPagination from 'components/Common/OffsetPagination'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { customSortCaret } from 'helpers/bootstrap_table_helper'
import { REPORTING_TIMEZONE } from 'helpers/sf_constants'
import { forColumnHeaderWrapper, getIANATimezoneStringForAbbreviation, millisecondsToSecondsFormatter, SfCommaStringFormatter } from 'helpers/sf_utils'
import LoadingDashboardTableSkeleton from 'pages/Dashboard/LoadingDashboardTableSkeleton'
import React, { useMemo } from 'react'
import { useState } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider, { CSVExport } from 'react-bootstrap-table2-toolkit'
import { useSelector } from 'react-redux'
import { Card, CardBody, Col, Form, Row, TabContent, TabPane } from 'reactstrap'
import { dateCreatedFormatter } from '../Users/UserComponents/UserList/Formatters'
import ReqAndResModal from './ReqAndResModal'

const DevDashboardTable = ({
  selectedTags,
  applyFiltersOnApiTimingsData,
  onApiTimingSortChange,
  onExportApiTimingsData,
  apiTimingsExportLoading = false,
  shouldUseOffsetPagination = false
}) => {

  const DEV_DASHBOARD_TABS = {
    TAGS: '1',
    API_REQUESTS: '2'
  }

  const {
    current_user,
    profiling_tags,
    profiling_tags_loading,
    aggr_group_by_tags,
    aggr_group_by_tags_loading,
    current_role_permissions_state,
    api_timings_data,
    api_timings_data_loading,
    last_page_fetched,
    has_next_page,
    has_lookahead_pages,
    total_number_of_pages,
  } = useSelector((state) => ({
    current_user: state.LoginAdminUser.current_user,
    profiling_tags: state.DevDashboard.profiling_tags,
    aggr_group_by_tags: state.DevDashboard.aggr_group_by_tags,
    profiling_tags_loading: state.DevDashboard.profiling_tags_loading,
    aggr_group_by_tags_loading: state.DevDashboard.aggr_group_by_tags_loading,
    current_role_permissions_state: state.Role.current_role_permissions_state,
    last_page_fetched: state.DevDashboard.last_page_fetched,
    has_next_page: state.DevDashboard.has_next_page,
    has_lookahead_pages: state.DevDashboard.has_lookahead_pages,
    total_number_of_pages: state.DevDashboard.total_number_of_pages,
    api_timings_data: state.DevDashboard.api_timings_data,
    api_timings_data_loading: state.DevDashboard.api_timings_data_loading,
  }));

  const { ExportCSVButton } = CSVExport;

  //states
  const [customActiveTab, setCustomActiveTab] = useState(DEV_DASHBOARD_TABS.TAGS);
  const [responseOrRequestModalState, setResponseOrRequestModalState] = useState({ active: false, row: null });
  const [apiTimingSort, setApiTimingSort] = useState({
    dataField: 'created_at',
    order: 'desc'
  });

  const tagsNameFormatter = (cellContent, _row, _rowIndex, profiling_tags) => {
    let name = 'Untagged Api'
    if (cellContent != null) {
      name = profiling_tags.find(tag => tag.id === cellContent).name;
    }
    return name;
  }

  const tagsColumns = [
    {
      dataField: 'api_profiling_tag_id',
      text: 'Name',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: tagsNameFormatter,
      formatExtraData: profiling_tags,
      csvFormatter: tagsNameFormatter
    },
    {
      dataField: 'total_requests',
      text: 'Total Requests',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: SfCommaStringFormatter,
      csvFormatter: SfCommaStringFormatter
    },
    {
      dataField: 'total_timeouts',
      text: 'Total Timeouts',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: SfCommaStringFormatter,
      csvFormatter: SfCommaStringFormatter
    }
  ]
  const tagsDefaultSorted = [{
    dataField: 'total_requests',
    order: 'desc'
  }]

  function handleOnclickView(row) {
    setResponseOrRequestModalState({ active: true, row: row })
  }

  function onClickCloseModal() {
    setResponseOrRequestModalState({ active: false, row: null })
  }

  function controllerNameFormatter(cellContent) {
    const apiPrefix = "Api::"
    const startIndex = cellContent?.indexOf(apiPrefix);
    if (startIndex !== -1) {
      return cellContent?.substring(startIndex + apiPrefix.length);
    }
    // If the prefix is not found, return the original string
    return cellContent;
  }

  const apiColumns = [
    {
      dataField: 'id',
      text: 'ID',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: (cellContent, row) => {
        return (
          <div
            className='text-sfblue'
            style={{ cursor: 'pointer' }}
            onClick={() => handleOnclickView(row)}
          >
            {cellContent}
          </div>
        )
      }
    },
    {
      dataField: 'action_name',
      text: 'Action Name',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
    },
    {
      dataField: 'controller_name',
      text: 'Controller Name',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: controllerNameFormatter,
      csvFormatter: controllerNameFormatter
    },
    {
      dataField: 'created_at',
      text: 'Received',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: dateCreatedFormatter,
      csvFormatter: (cellContent) => (cellContent ? format(utcToZonedTime(cellContent, getIANATimezoneStringForAbbreviation(REPORTING_TIMEZONE)), 'MM/dd/yyyy hh:mm aa') : '--'),
    },
    {
      dataField: 'elapsed_time',
      text: 'Elapsed Time(in sec)',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: millisecondsToSecondsFormatter,
      csvFormatter: millisecondsToSecondsFormatter
    },
    {
      dataField: 'db_runtime',
      text: 'DB RunTime(in sec)',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: millisecondsToSecondsFormatter,
      csvFormatter: millisecondsToSecondsFormatter
    },
    {
      dataField: 'response_code',
      text: 'Response Code',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: (cellContent) => cellContent || '--',
      csvFormatter: (cellContent) => cellContent || '--'
    },
  ]

  const apisDefaultSorted = [{
    dataField: apiTimingSort.dataField,
    order: apiTimingSort.order
  }]

  const handleApiTimingTableChange = (type, { sortField, sortOrder }) => {
    if (type !== 'sort') return
    if (sortField === apiTimingSort.dataField && sortOrder === apiTimingSort.order) return

    setApiTimingSort({ dataField: sortField, order: sortOrder })
    onApiTimingSortChange?.(sortField, sortOrder)
  }

  const currentPage = Math.max(1, Number(last_page_fetched) || 1)
  const knownTotalPages = Math.max(Number(total_number_of_pages) || 0, currentPage)
  const paginationTotal = useMemo(() => (
    shouldUseOffsetPagination
      ? (knownTotalPages > 0
        ? knownTotalPages
        : (has_next_page
          ? currentPage + (has_lookahead_pages ? 8 : 1)
          : currentPage))
      : Math.max(Number(total_number_of_pages) || 0, currentPage)
  ), [shouldUseOffsetPagination, knownTotalPages, has_next_page, currentPage, has_lookahead_pages, total_number_of_pages])

  const onApiTimingsPageChange = (page) => {
    const parsedPage = Number(page)
    if (!Number.isFinite(parsedPage) || parsedPage < 1 || api_timings_data_loading) {
      return
    }
    applyFiltersOnApiTimingsData(selectedTags, 0, {
      page_number: parsedPage,
      appendResults: false,
    })
  }

  return (
    <React.Fragment>
      <ReqAndResModal
        show={responseOrRequestModalState}
        onClickCloseModal={onClickCloseModal}
      />
      <Row>
        <Col>
          <div className="d-flex justify-content-between">
            <ul className="nav sfnav mb-4">
              <li className="nav-item">
                <SfNavLink position={DEV_DASHBOARD_TABS.TAGS} location='#tags' tabName="Tags" customActiveTab={customActiveTab} setcustomActiveTab={setCustomActiveTab} />
              </li>
              <li className="nav-item">
                <SfNavLink position={DEV_DASHBOARD_TABS.API_REQUESTS} location='#api_requests' tabName="Api Requests" customActiveTab={customActiveTab} setcustomActiveTab={setCustomActiveTab} />
              </li>
            </ul>
          </div>
          <TabContent activeTab={customActiveTab}>
            {(profiling_tags_loading || aggr_group_by_tags_loading) ? <LoadingDashboardTableSkeleton rows={6} cols={aggr_group_by_tags?.length} /> : (
              <>
                <TabPane tabId="1">
                  <Row>
                    <Col lg="12">
                      <Card>
                        <CardBody>
                          <ToolkitProvider
                            keyField='id'
                            columns={tagsColumns}
                            data={aggr_group_by_tags}
                            exportCSV={{
                              fileName: "Tags.csv",
                              exportAll: false,
                            }}
                          >
                            {
                              toolkitProps => (
                                <React.Fragment>
                                  <Form>
                                    <Col xl="auto" className="ms-auto d-flex align-items-end justify-content-end">
                                      <div className="mb-2">
                                        <div>
                                          <ExportCSVButton className="btn btn-outline-sfblue" {...toolkitProps.csvProps}>
                                            <i className="bx bx-import font-size-14 align-middle me-2" />
                                            Export
                                          </ExportCSVButton>
                                        </div>
                                      </div>
                                    </Col>
                                  </Form>
                                  <Row className="sfTable-Container" style={{zIndex:1}}>
                                    <Col xl="12">
                                      {aggr_group_by_tags_loading ? (
                                        <LoadingDashboardTableSkeleton rows={6} cols={tagsColumns?.length} />
                                      ) : (
                                        <BootstrapTable
                                          keyField={"id"}
                                          responsive
                                          bordered={false}
                                          striped={false}
                                          defaultSorted={tagsDefaultSorted}
                                          rowClasses="tr-hover"
                                          headerClasses="sftableHeader thead-h50 text-black"
                                          classes="table align-middle table-nowrap text-black"
                                          headerWrapperClasses={"thead-light"}
                                          noDataIndication="You currently don't have any data"
                                          sort={{ sortCaret: customSortCaret }}
                                          {...toolkitProps.baseProps}
                                        />
                                      )}
                                    </Col>
                                  </Row>
                                </React.Fragment>
                              )
                            }
                          </ToolkitProvider>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabId="2">
                  <Row>
                    <Col lg="12">
                      <Card>
                        <CardBody>
                          <ToolkitProvider
                            keyField='id'
                            columns={apiColumns}
                            data={api_timings_data}
                            exportCSV={{
                              fileName: "ApiRequests.csv",
                              exportAll: false,
                            }}
                          >
                            {
                              toolkitProps => (
                                <React.Fragment>
                                  <Form>
                                    <Col xl="auto" className="ms-auto d-flex align-items-end justify-content-end">
                                      <div className="mb-2">
                                        <div>
                                          <button
                                            type="button"
                                            className="btn btn-outline-sfblue"
                                            disabled={apiTimingsExportLoading || !api_timings_data?.length}
                                            onClick={onExportApiTimingsData}
                                          >
                                            <i className="bx bx-export font-size-14 align-middle me-2" />
                                            {apiTimingsExportLoading ? 'Queuing...' : 'Export'}
                                          </button>
                                        </div>
                                      </div>
                                    </Col>
                                  </Form>
                                  <Row className="sfTable-Container">
                                    <Col xl="12">
                                      {!api_timings_data?.length > 0 && api_timings_data_loading ? (
                                        <LoadingDashboardTableSkeleton rows={6} cols={apiColumns?.length} />
                                      ) : (
                                        <BootstrapTable
                                          keyField={"id"}
                                          responsive
                                          bordered={false}
                                          striped={false}
                                          defaultSorted={apisDefaultSorted}
                                          remote={{ sort: true }}
                                          onTableChange={handleApiTimingTableChange}
                                          rowClasses="tr-hover"
                                          headerClasses="sftableHeader thead-h50 text-black"
                                          classes="table align-middle table-nowrap text-black"
                                          headerWrapperClasses={"thead-light"}
                                          noDataIndication="You currently don't have any data"
                                          sort={{ sortCaret: customSortCaret }}
                                          {...toolkitProps.baseProps}
                                        />
                                      )}
                                    </Col>
                                  </Row>
                                  {shouldUseOffsetPagination ? (
                                    <OffsetPagination
                                      currentPage={currentPage}
                                      totalPages={paginationTotal}
                                      hasNextPage={has_next_page}
                                      hasDeepOffsetPages={has_lookahead_pages}
                                      offsetLookAheadPages={8}
                                      loading={api_timings_data_loading}
                                      onPageChange={onApiTimingsPageChange}
                                      className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                    />
                                  ) : (
                                    <Row>
                                      <Col xl='auto' className='ms-auto'>
                                        {has_next_page && (
                                          <button
                                            type="button"
                                            className='btn btn-outline-sfblue'
                                            onClick={() => applyFiltersOnApiTimingsData(selectedTags, last_page_fetched)}
                                            disabled={api_timings_data_loading}
                                          >
                                            {api_timings_data_loading ? 'Please wait...' : 'Load More'}
                                          </button>
                                        )}
                                      </Col>
                                    </Row>
                                  )}
                                </React.Fragment>
                              )
                            }
                          </ToolkitProvider>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </TabPane>
              </>
            )}
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default DevDashboardTable
